import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash/fp';
import * as actions from 'actions/pathBuilder/contents';
import AddCriteria from 'components/FeedbackCriteria/AddCriteria';
import FeedbackCriteria from 'components/FeedbackCriteria/FeedbackCriteria';
import DebouncedTextArea from 'components/shared/debouncedTextArea';
import { routes, TYPES } from 'constants/config';
import useAutosize from 'hooks/useAutosize';
import { Icon } from 'ui';
import request from 'utils/request_ajax';

const criteria = [
  {
    icon: 'rating-trophy',
    text: t('grades.ratings.exceptional'),
    key: 0,
  },
  {
    icon: 'rating-thumbs-up',
    text: t('grades.ratings.exceeds_expectations'),
    key: 1,
  },
  {
    icon: 'rating-check',
    text: t('grades.ratings.meets_expectations'),
    key: 2,
  },
  {
    icon: 'rating-wrench',
    text: t('grades.ratings.needs_improvement'),
    key: 3,
  },
  {
    icon: 'rating-warn',
    text: t('grades.ratings.unsatisfactory'),
    key: 4,
  },
];

const DemonstrationPromptCard = ({
  updateDemonstration,
  id,
  canUseCustomFeedbackCriteria,
  demonstration,
}) => {
  const [prompt, setPrompt] = useState(demonstration.prompt);
  const [noteToGraders, setNoteToGraders] = useState(
    demonstration.noteToGraders,
  );
  const [feedbackCriteria, setSelectedCriteria] = useState(
    demonstration.feedbackCriteria,
  );
  const autosizeRef = useAutosize();

  const onSave = (attributes = { prompt, noteToGraders }) => {
    request
      .put(routes.UPDATE_DEMONSTRATION(id), {
        demonstration: attributes,
      })
      .then(response => {
        updateDemonstration(id, { ...attributes, id });
      })
      .catch(error => {
        console.error(error);
      });
  };

  const debouncedOnSave = useRef(debounce(300, onSave)).current;

  const handleInputChange = value => {
    setPrompt(value);
    debouncedOnSave({ prompt: value });
  };

  const updateFeedbackCriteria = selectedCriteria => {
    const updatedDemonstration = {
      feedbackCriterionIds: selectedCriteria.map(criterion => criterion.id),
    };
    request
      .put(routes.UPDATE_DEMONSTRATION(id), {
        demonstration: updatedDemonstration,
      })
      .then(() => {
        setSelectedCriteria(selectedCriteria);
        updateDemonstration(id, { feedbackCriteria: selectedCriteria });
      })
      .catch(error => {
        console.error(error);
      });
  };

  const handleNoteToGraders = value => {
    setNoteToGraders(value);
    debouncedOnSave({ noteToGraders: value });
  };

  return (
    <div className="Card bg-white pt-16 mt-24">
      <div className="d-f fd-c px-32 pb-32">
        <header className="DemonstrationViewerHeader py-16">
          <h1 className="ff-ss fz-24 mb-8 mt-8 lh-1">
            <span className="text-wrap">{`${t(
              'demonstrations.create_modal.type',
            )}: ${t('demonstrations.submit_link')}`}</span>
          </h1>
        </header>
        <header className="pb-8">
          <span className="text-wrap fz-bigger ff-ss mb-12 lh-24">
            {t('demonstrations.demonstration_builder.submit_link_instructions')}
          </span>
        </header>
        <div className="d-f fd-r">
          <div className="d-f fd-c pt-24 w-fill">
            <header className="pb-8">
              <span className="DemonstrationBuilderTitle text-wrap fz-bigger ff-ss fw-b mb-12">
                {t('demonstrations.demonstration_builder.prompt_label')}
              </span>
            </header>
            <DebouncedTextArea
              id="prompt"
              name="prompt"
              className="DemonstrationBuilderPromptInput ff-ss"
              placeholder={t(
                'demonstrations.demonstration_builder.prompt_placeholder',
              )}
              onChange={handleInputChange}
              value={prompt}
              ref={autosizeRef}
              singleLine
            />
            {feedbackCriteria.length > 0 ? (
              <FeedbackCriteria
                selectedCriteria={feedbackCriteria}
                handleUpdate={updateFeedbackCriteria}
                canUseCustomFeedbackCriteria={canUseCustomFeedbackCriteria}
              />
            ) : (
              <AddCriteria
                canUseCustomFeedbackCriteria={canUseCustomFeedbackCriteria}
                handleUpdate={updateFeedbackCriteria}
              />
            )}
          </div>
        </div>
        <div className="d-f fd-r">
          <div className="d-f fd-c pt-24 w-fill">
            <header className="pb-8">
              <span className="DemonstrationBuilderTitle text-wrap fz-bigger ff-ss fw-b mb-12">
                {t(
                  'demonstrations.demonstration_builder.instructions_to_graders',
                )}
              </span>
            </header>
            <span className="fz-12 c-brand-gray-darkest lh-16 pb-16">
              {t('demonstrations.demonstration_builder.note_to_grader')}
            </span>
            {criteria.map(criterium => (
              <span className="pb-8 c-brand-gray pb-16" key={criterium.key}>
                <Icon name={criterium.icon} />
                <span className="pl-8 c-brand-gray-darkest">
                  {criterium.text}
                </span>
              </span>
            ))}
            <DebouncedTextArea
              id="noteToGraders"
              name="noteToGraders"
              className="DemonstrationBuilderPromptInput ff-ss"
              placeholder={t(
                'demonstrations.demonstration_builder.note_to_grader_placeholder',
              )}
              onChange={handleNoteToGraders}
              value={noteToGraders}
              ref={autosizeRef}
              singleLine
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ meta }) => ({
  canUseCustomFeedbackCriteria: meta.features.customFeedbackCriteria,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateDemonstration: (id, demonstration) =>
    dispatch(actions.update(id, TYPES.DEMONSTRATION, demonstration)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DemonstrationPromptCard);
